import React, { useState, useEffect, useContext } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../App';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { CheckCircle, Loader2, Heart, Home, AlertCircle } from 'lucide-react';

const TipSuccess = () => {
  const [searchParams] = useSearchParams();
  const { API } = useContext(AuthContext);
  const [status, setStatus] = useState('checking'); // checking, success, failed
  const [transaction, setTransaction] = useState(null);
  const [pollCount, setPollCount] = useState(0);
  
  const sessionId = searchParams.get('session_id');
  
  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus();
    }
  }, [sessionId]);
  
  const pollPaymentStatus = async () => {
    if (pollCount >= 5) {
      setStatus('timeout');
      return;
    }
    
    try {
      const response = await axios.get(`${API}/tips/status/${sessionId}`);
      
      if (response.data.payment_status === 'paid') {
        setStatus('success');
        setTransaction(response.data.transaction);
      } else if (response.data.status === 'expired') {
        setStatus('failed');
      } else {
        // Continue polling
        setPollCount(prev => prev + 1);
        setTimeout(pollPaymentStatus, 2000);
      }
    } catch (error) {
      console.error('Error checking payment status:', error);
      setStatus('failed');
    }
  };
  
  return (
    <div className="min-h-screen hero-background flex items-center justify-center p-4">
      <Card className="max-w-md w-full bg-gray-800/90 backdrop-blur border-purple-500/30">
        <CardContent className="p-8 text-center">
          {status === 'checking' && (
            <>
              <Loader2 className="w-16 h-16 mx-auto text-purple-400 animate-spin mb-4" />
              <h2 className="text-2xl font-bold text-white mb-2">Processing Payment</h2>
              <p className="text-gray-400">Please wait while we confirm your tip...</p>
            </>
          )}
          
          {status === 'success' && (
            <>
              <div className="w-20 h-20 mx-auto mb-4 bg-green-500/20 rounded-full flex items-center justify-center">
                <CheckCircle className="w-12 h-12 text-green-400" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-2">Thank You!</h2>
              <p className="text-gray-400 mb-4">Your tip was sent successfully!</p>
              
              {transaction && (
                <div className="bg-gray-700/50 rounded-lg p-4 mb-6 text-left">
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-gray-400">Amount:</span>
                    <span className="text-white font-semibold">${transaction.amount?.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-gray-400">Creator receives:</span>
                    <span className="text-green-400 font-semibold">${transaction.creator_amount?.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-400">Platform fee (5%):</span>
                    <span className="text-gray-500">${transaction.platform_fee?.toFixed(2)}</span>
                  </div>
                </div>
              )}
              
              <div className="flex items-center justify-center space-x-2 text-pink-400 mb-6">
                <Heart className="w-5 h-5 fill-pink-400" />
                <span>You're awesome for supporting creators!</span>
              </div>
              
              <Link to="/">
                <Button className="bg-purple-600 hover:bg-purple-700 w-full">
                  <Home className="w-4 h-4 mr-2" />
                  Back to Home
                </Button>
              </Link>
            </>
          )}
          
          {(status === 'failed' || status === 'timeout') && (
            <>
              <div className="w-20 h-20 mx-auto mb-4 bg-red-500/20 rounded-full flex items-center justify-center">
                <AlertCircle className="w-12 h-12 text-red-400" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-2">Payment Issue</h2>
              <p className="text-gray-400 mb-6">
                {status === 'timeout' 
                  ? 'Payment status check timed out. Please check your email for confirmation.'
                  : 'There was an issue with your payment. No charge was made.'}
              </p>
              
              <Link to="/">
                <Button className="bg-purple-600 hover:bg-purple-700 w-full">
                  <Home className="w-4 h-4 mr-2" />
                  Back to Home
                </Button>
              </Link>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default TipSuccess;
